/**
 * Utility functions for localStorage operations and data parsing
 *
 * This module provides safe access to browser localStorage and utilities for parsing
 * comma-separated values stored in localStorage. It includes specific helpers for
 * retrieving recent search terms and recently viewed items.
 *
 * All localStorage operations are wrapped with error handling to prevent failures
 * in environments where localStorage may be unavailable or restricted.
 */

const parseCommaSeparatedArray = (value: string | null, limit?: number): string[] => {
    if (!value) return [];

    return value
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean)
        .slice(0, limit);
};

const safeGetFromStorage = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

/**
 * Generic function to get and parse a comma-separated array from localStorage
 * @param storageKey - The localStorage key to retrieve data from
 * @param limit - Optional limit for the number of items to return
 * @returns Array of parsed strings
 */
export function getParsedStorageArray(storageKey: string, limit?: number): string[] {
    const data = safeGetFromStorage(storageKey);
    return parseCommaSeparatedArray(data, limit);
}

export function getLastRecentSearchTerms(limit?: number): string[] {
    return getParsedStorageArray('search:autocomplete:history', limit);
}

export function getLastRecentViewsList(limit?: number): string[] {
    return getParsedStorageArray('user_recent_views', limit);
}
